import { TypedDocumentNode, gql } from '@apollo/client';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { To } from 'react-router-dom';
import styled from 'styled-components';

import {
  Rarity,
  So5LeaderboardSeasonality,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { SeasonalityIcon } from '@sorare/core/src/atoms/icons/SeasonalityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { range } from '@sorare/core/src/lib/arrays';

import { LeaderboardComposable } from 'components/leaderboard/LeaderboardComposable';
import { getComposeTeamRoute } from 'lib/so5';

import { LeagueTrckSlot } from './LeagueTrckSlot';
import type { LeagueTrckSlots_so5LeagueTrack } from './__generated__/index.graphql';

const List = styled(Horizontal).attrs({ center: true })`
  overflow: auto;
  padding: 0 var(--unit);
  max-width: 100%;
`;
const StyledButtonBase = styled(ButtonBase)`
  &.IN_SEASON {
    order: -1;
    ${Horizontal} {
      flex-direction: row-reverse;
    }
  }
`;

type Props = {
  so5LeagueTrack: LeagueTrckSlots_so5LeagueTrack;
  switchSo5LeagueTrack?: {
    slug: string;
    seasonality: So5LeaderboardSeasonality;
    to: To;
  };
};

export const LeagueTrckSlots = ({
  so5LeagueTrack,
  switchSo5LeagueTrack,
}: Props) => {
  const { formatMessage } = useIntlContext();
  const { sport } = useSportContext();

  const {
    maxManagerTeamsCount,
    entrySo5Leaderboard,
    so5Leaderboards,
    completed,
  } = so5LeagueTrack;

  const managerTeamBased = maxManagerTeamsCount > 0;
  const nbSlots = managerTeamBased
    ? maxManagerTeamsCount
    : entrySo5Leaderboard.teamsCap || 0;

  const contenders = so5Leaderboards.flatMap(
    so5Leaderboard => so5Leaderboard.mySo5LeaderboardContenders
  );

  // Get the first slot that is not composed with or without a contender
  const nbComposedSlots = contenders.filter(
    contender => contender.so5Lineup
  ).length;
  const firstNotComposedSlot =
    nbComposedSlots === nbSlots ? 0 : nbComposedSlots;

  const [selectedIndex, setSelectedIndex] =
    useState<number>(firstNotComposedSlot);

  const selectedContender = contenders.at(selectedIndex);

  const firstEmptySlot = contenders.length;

  let label: ReactNode;
  let locked = false;
  if (completed) {
    label = formatMessage({
      id: 'leagueTrck.LeagueTrckSlots.alreadyCompletedTutorial',
      defaultMessage: 'You already completed this tutorial',
    });
  } else if (selectedContender?.so5Lineup) {
    label = formatMessage({
      id: 'leagueTrck.LeagueTrckSlots.editMyTeam',
      defaultMessage: 'Edit my Team',
    });
  } else if (selectedContender?.managerTeam?.hidden) {
    label = formatMessage({
      id: 'leagueTrck.LeagueTrckSlots.reactiveMyTeam',
      defaultMessage: 'Re-activate Team',
    });
  } else if (selectedContender?.managerTeam) {
    label = formatMessage({
      id: 'leagueTrck.LeagueTrckSlots.setMyLineup',
      defaultMessage: 'Set my Lineup',
    });
  } else if (
    firstEmptySlot === selectedIndex &&
    entrySo5Leaderboard.canCompose.value
  ) {
    label = formatMessage({
      id: 'leagueTrck.LeagueTrckSlots.addATeam',
      defaultMessage: 'Add a Team',
    });
  } else {
    locked = true;
    label = entrySo5Leaderboard.canCompose.value ? (
      formatMessage({
        id: 'leagueTrck.LeagueTrckSlots.createPriorTeamsFirst',
        defaultMessage: 'Create prior teams first',
      })
    ) : (
      <LeaderboardComposable
        labelOnly
        so5Leaderboard={entrySo5Leaderboard}
        so5LeagueTrack={so5LeagueTrack}
      />
    );
  }

  const disabled = completed || locked;

  return (
    <>
      <List gap={3}>
        {range(nbSlots).map(i => {
          return (
            <LeagueTrckSlot
              key={i}
              index={i}
              selected={i === selectedIndex}
              onClick={() => setSelectedIndex(i)}
              canCompose={entrySo5Leaderboard.canCompose.value}
              firstEmptySlot={firstEmptySlot === i}
              so5LeaderboardContender={contenders.at(i)}
              rarity={entrySo5Leaderboard.mainRarityType || Rarity.common}
              completed={!!completed}
            />
          );
        })}
        {switchSo5LeagueTrack && (
          <StyledButtonBase
            to={switchSo5LeagueTrack.to}
            className={classNames(switchSo5LeagueTrack.seasonality)}
          >
            <Horizontal as="span">
              <SeasonalityIcon
                size={2.5}
                seasonality={switchSo5LeagueTrack.seasonality}
              />
              <FontAwesomeIcon
                icon={
                  switchSo5LeagueTrack.seasonality ===
                  So5LeaderboardSeasonality.ALL_SEASONS
                    ? faAngleRight
                    : faAngleLeft
                }
              />
            </Horizontal>
          </StyledButtonBase>
        )}
      </List>
      <Button
        fullWidth
        size="large"
        color={selectedContender?.so5Lineup ? 'secondary' : 'primary'}
        disabled={disabled}
        to={
          !disabled
            ? getComposeTeamRoute({
                leaderboardSlug:
                  selectedContender?.so5Leaderboard.slug ||
                  entrySo5Leaderboard.slug,
                lineupId: selectedContender?.so5Lineup
                  ? selectedContender.so5Lineup.id
                  : undefined,
                managerTeamId: selectedContender?.managerTeam
                  ? selectedContender.managerTeam.id
                  : undefined,
                sport,
                eventType: entrySo5Leaderboard.so5Fixture.type.toLowerCase(),
              })
            : undefined
        }
      >
        {label}
      </Button>
    </>
  );
};

LeagueTrckSlots.fragments = {
  so5LeagueTrack: gql`
    fragment LeagueTrckSlots_so5LeagueTrack on So5LeagueTrack {
      slug
      maxManagerTeamsCount
      completed
      entrySo5Leaderboard {
        slug
        teamsCap
        mainRarityType
        canCompose {
          value
        }
        so5Fixture {
          slug
          type
        }
        ...LeaderboardComposable_so5Leaderboard
      }
      so5Leaderboards {
        slug
        mySo5LeaderboardContenders(includeHiddenManagerTeam: true) {
          slug
          so5Lineup {
            id
          }
          managerTeam {
            id
            hidden
          }
          ...LeagueTrckSlot_so5LeaderboardContender
        }
      }
      ...LeaderboardComposable_so5LeagueTrack
    }
    ${LeagueTrckSlot.fragments.so5LeaderboardContender}
    ${LeaderboardComposable.fragments.so5Leaderboard}
    ${LeaderboardComposable.fragments.so5LeagueTrack}
  ` as TypedDocumentNode<LeagueTrckSlots_so5LeagueTrack>,
};
